import React, { useEffect, useState, useCallback } from "react";
import { FlaskConical, Users, ClipboardList, FileUp, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const KIND_META = {
  study:        { label: "Study",         icon: FlaskConical,   tone: "sky" },
  enrolment:    { label: "Enrolment",     icon: Users,          tone: "mint" },
  safety_event: { label: "Safety event",  icon: ClipboardList,  tone: "peach" },
  bulk_upload:  { label: "Bulk upload",   icon: FileUp,         tone: "lavender" },
};

export default function CroReviewQueue() {
  const [data, setData] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/cro/intake?status=pending&limit=20");
      setData(res.data);
    } catch { setData({ count: 0, items: [] }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const res = await api.post(`/cro/intake/${id}/approve`);
      const promoted = res.data.promoted_study;
      toast.success(promoted ? `Promoted to portfolio: ${promoted.id}` : "Approved");
      await load();
    } catch (e) {
      toast.error("Approve failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusyId(null); }
  };

  const doReject = async (reason) => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.id);
    try {
      await api.post(`/cro/intake/${rejectTarget.id}/reject`, { reason });
      toast.success("Rejected");
      setRejectTarget(null);
      await load();
    } catch (e) {
      toast.error("Reject failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusyId(null); }
  };

  if (!data) return null;

  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="admin-cro-review-queue">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">CRO Intake · Admin review</div>
          <div className="font-serif text-lg text-primary">{data.count} pending record{data.count === 1 ? "" : "s"}</div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-review-refresh">Refresh</Button>
      </div>

      {data.items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Queue is empty — nothing awaiting review.</div>
      ) : (
        <ul className="divide-y divide-border" data-testid="admin-review-list">
          {data.items.map((it) => {
            const meta = KIND_META[it.kind] || { label: it.kind, icon: ClipboardList, tone: "sky" };
            const Icon = meta.icon;
            const p = it.payload || {};
            return (
              <li key={it.id} className="py-3" data-testid={`admin-review-item-${it.id}`}>
                <div className="flex items-start gap-3">
                  <span className={`shrink-0 w-9 h-9 rounded-md bg-pastel-${meta.tone}-bg text-pastel-${meta.tone}-fg inline-flex items-center justify-center`}>
                    <Icon size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-primary flex items-center gap-2 flex-wrap">
                      <span>{meta.label}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="truncate">{summarise(it)}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {new Date(it.submitted_at).toLocaleString("en-IN")} · {it.submitted_by?.email} ({it.submitted_by?.role})
                    </div>
                    <details className="mt-1.5 text-[11px]">
                      <summary className="cursor-pointer text-muted-foreground hover:text-primary">Full payload</summary>
                      <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto">{JSON.stringify(p, null, 2)}</pre>
                    </details>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === it.id}
                      onClick={() => setRejectTarget(it)}
                      data-testid={`admin-reject-${it.id}`}
                      className="text-destructive border-destructive/40 hover:bg-destructive/10"
                    >
                      <X size={13} /> Reject
                    </Button>
                    <Button
                      size="sm"
                      disabled={busyId === it.id}
                      onClick={() => approve(it.id)}
                      data-testid={`admin-approve-${it.id}`}
                    >
                      {busyId === it.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <RejectDialog target={rejectTarget} onCancel={() => setRejectTarget(null)} onConfirm={doReject} />
    </div>
  );
}

function RejectDialog({ target, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (!target) setReason(""); }, [target]);
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="admin-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject intake record</DialogTitle>
          <DialogDescription>
            {target ? `${KIND_META[target.kind]?.label} · ${summarise(target)}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Textarea
            rows={3}
            data-testid="admin-reject-reason"
            placeholder="Reason (e.g. duplicate, missing data, wrong format)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={reason.trim().length < 3}
            onClick={() => onConfirm(reason.trim())}
            data-testid="admin-reject-confirm"
          >
            Confirm reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function summarise(it) {
  const p = it.payload || {};
  switch (it.kind) {
    case "study":        return `${p.name} · ${p.phase} · ${p.therapeutic_area}`;
    case "enrolment":    return `${p.study_id} · ${p.site_city} · scr ${p.screened} → rand ${p.randomised}`;
    case "safety_event": return `${p.study_id} · ${p.severity} · ${p.event_term}`;
    case "bulk_upload":  return `${p.study_id} · ${p.dataset} · ${p.row_count} rows`;
    default:             return it.id;
  }
}
